// Loan Compliance Module v1.0 PR 3 — detail drawer for a single
// MaterialEvent. Surfaces append-only notice history + the four action
// buttons (Route notice, Set disposition, Escalate to exception, Link
// payment LoanEvent).

import { useState } from "react";
import { Badge, Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import type {
  MaterialEventDisposition,
  MaterialEventDto,
  RouteMaterialEventNoticeRequest,
  SetMaterialEventDispositionRequest,
} from "@/api/compliance/types";
import {
  useEscalateMaterialEvent,
  useLinkMaterialEventLoanEvent,
  useMaterialEventNotices,
  useRouteMaterialEventNotice,
  useSetMaterialEventDisposition,
} from "@/hooks/compliance/useCompliance";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { formatDate } from "../common/colorBand";
import {
  MATERIAL_EVENT_DISPOSITION_LABELS,
  MATERIAL_EVENT_SOURCE_LABELS,
  MATERIAL_EVENT_TYPE_LABELS,
} from "./labels";

interface MaterialEventDrawerProps {
  loanId: string;
  event: MaterialEventDto;
  canManage: boolean;
  open: boolean;
  onClose: () => void;
}

const DISPOSITIONS: MaterialEventDisposition[] = [
  "Open",
  "UnderReview",
  "NoticeRequired",
  "NoticeSent",
  "Resolved",
];

type Panel = "none" | "notice" | "disposition" | "escalate" | "link";

function severityBadge(sev: string) {
  if (sev === "Critical") return "error" as const;
  if (sev === "Material") return "review" as const;
  if (sev === "Process") return "info" as const;
  return "archived" as const;
}

export function MaterialEventDrawer({
  loanId,
  event,
  canManage,
  open,
  onClose,
}: MaterialEventDrawerProps) {
  const toast = useToast();
  const notices = useMaterialEventNotices(event.id);
  const routeNotice = useRouteMaterialEventNotice(loanId, event.id);
  const setDisposition = useSetMaterialEventDisposition(loanId, event.id);
  const escalate = useEscalateMaterialEvent(loanId, event.id);
  const linkLoanEvent = useLinkMaterialEventLoanEvent(loanId, event.id);

  const [panel, setPanel] = useState<Panel>("none");
  const [error, setError] = useState<string | null>(null);

  // Notice form
  const [notifiedParty, setNotifiedParty] = useState("");
  const [requiredByDate, setRequiredByDate] = useState("");
  const [channel, setChannel] = useState("");
  const [noticeNote, setNoticeNote] = useState("");

  // Disposition form
  const [newDisposition, setNewDisposition] =
    useState<MaterialEventDisposition>(event.disposition);
  const [dispositionNote, setDispositionNote] = useState("");

  // Escalate form
  const [citation, setCitation] = useState("");
  const [remediationPlan, setRemediationPlan] = useState("");

  // Link form
  const [loanEventId, setLoanEventId] = useState("");

  const surfaceError = (err: unknown, fallback: string) => {
    const msg =
      err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : fallback;
    setError(msg);
    toast.error(msg);
  };

  const submitNotice = async () => {
    setError(null);
    if (!notifiedParty.trim()) {
      setError("Notified party is required.");
      return;
    }
    const body: RouteMaterialEventNoticeRequest = {
      notifiedParty: notifiedParty.trim(),
      requiredByDate: requiredByDate || null,
      channel: channel.trim() || null,
      note: noticeNote.trim() || null,
    };
    try {
      await routeNotice.mutateAsync(body);
      toast.success("Notice recorded.");
      setPanel("none");
      setNotifiedParty("");
      setRequiredByDate("");
      setChannel("");
      setNoticeNote("");
    } catch (err) {
      surfaceError(err, "Could not record notice.");
    }
  };

  const submitDisposition = async () => {
    setError(null);
    const body: SetMaterialEventDispositionRequest = {
      disposition: newDisposition,
      note: dispositionNote.trim() || null,
    };
    try {
      await setDisposition.mutateAsync(body);
      toast.success("Disposition updated.");
      setPanel("none");
      setDispositionNote("");
    } catch (err) {
      surfaceError(err, "Could not update disposition.");
    }
  };

  const submitEscalate = async () => {
    setError(null);
    if (!citation.trim()) {
      setError("Program-document citation is required.");
      return;
    }
    try {
      await escalate.mutateAsync({
        programDocumentCitation: citation.trim(),
        remediationPlan: remediationPlan.trim() || null,
      });
      toast.success("Escalated to compliance exception.");
      setPanel("none");
    } catch (err) {
      surfaceError(err, "Could not escalate.");
    }
  };

  const submitLink = async () => {
    setError(null);
    if (!loanEventId.trim()) {
      setError("Loan event ID is required.");
      return;
    }
    try {
      await linkLoanEvent.mutateAsync(loanEventId.trim());
      toast.success("Linked payment loan event.");
      setPanel("none");
      setLoanEventId("");
    } catch (err) {
      surfaceError(err, "Could not link loan event.");
    }
  };

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={event.title}
      subtitle={`${MATERIAL_EVENT_TYPE_LABELS[event.eventType]} · ${MATERIAL_EVENT_SOURCE_LABELS[event.source]}`}
      width="lg"
      testId="material-event-drawer"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-space-4">
        <section>
          <div className="flex items-center gap-space-2 mb-space-2 flex-wrap">
            <Badge status={severityBadge(event.severity)}>
              {event.severity}
            </Badge>
            <Badge status="info">
              {MATERIAL_EVENT_DISPOSITION_LABELS[event.disposition] ??
                event.disposition}
            </Badge>
            {event.relatedComplianceExceptionId && (
              <Badge status="error">Exception opened</Badge>
            )}
          </div>
          <p className="text-small m-0 whitespace-pre-line">
            {event.description}
          </p>
          <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0 mt-space-2">
            <dt className="text-caption text-gray-mid uppercase tracking-label">
              Detected
            </dt>
            <dd className="m-0">{formatDate(event.dateDetected)}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">
              Reported by borrower
            </dt>
            <dd className="m-0">
              {formatDate(event.dateReportedByBorrower)}
            </dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">
              Notice obligation
            </dt>
            <dd className="m-0">{event.noticeObligation ? "Yes" : "No"}</dd>
            <dt className="text-caption text-gray-mid uppercase tracking-label">
              Linked loan event
            </dt>
            <dd className="m-0 font-mono text-caption">
              {event.relatedLoanEventId ?? "—"}
            </dd>
          </dl>
        </section>

        {canManage && (
          <section className="flex flex-wrap gap-space-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPanel("notice")}
              data-testid="material-event-route-notice"
            >
              Route notice
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPanel("disposition")}
              data-testid="material-event-set-disposition"
            >
              Set disposition
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPanel("escalate")}
              disabled={!!event.relatedComplianceExceptionId}
              data-testid="material-event-escalate"
            >
              Escalate to exception
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPanel("link")}
              data-testid="material-event-link-loan-event"
            >
              Link payment loan event
            </Button>
          </section>
        )}

        {panel === "notice" && (
          <section
            className="rounded-md border border-border-light p-space-3 flex flex-col gap-space-2"
            data-testid="material-event-notice-form"
          >
            <h4 className="font-display font-semibold text-h4 m-0">
              Route notice
            </h4>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Notified party
              </span>
              <input
                className="h-9 px-space-2 border border-border-light rounded-md text-small"
                value={notifiedParty}
                onChange={(e) => setNotifiedParty(e.target.value)}
                data-testid="material-event-notified-party"
              />
            </label>
            <div className="grid grid-cols-2 gap-space-2">
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">
                  Required by
                </span>
                <input
                  type="date"
                  className="h-9 px-space-2 border border-border-light rounded-md text-small"
                  value={requiredByDate}
                  onChange={(e) => setRequiredByDate(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">
                  Channel
                </span>
                <input
                  className="h-9 px-space-2 border border-border-light rounded-md text-small"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                />
              </label>
            </div>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">Note</span>
              <textarea
                className="min-h-[60px] px-space-2 py-space-1 border border-border-light rounded-md text-small"
                value={noticeNote}
                onChange={(e) => setNoticeNote(e.target.value)}
              />
            </label>
            <div className="flex gap-space-2 justify-end">
              <Button variant="tertiary" size="sm" onClick={() => setPanel("none")}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void submitNotice()}
                disabled={routeNotice.isPending}
                data-testid="material-event-notice-submit"
              >
                Record notice
              </Button>
            </div>
          </section>
        )}

        {panel === "disposition" && (
          <section
            className="rounded-md border border-border-light p-space-3 flex flex-col gap-space-2"
            data-testid="material-event-disposition-form"
          >
            <h4 className="font-display font-semibold text-h4 m-0">
              Set disposition
            </h4>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Disposition
              </span>
              <select
                className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
                value={newDisposition}
                onChange={(e) =>
                  setNewDisposition(e.target.value as MaterialEventDisposition)
                }
              >
                {DISPOSITIONS.map((d) => (
                  <option key={d} value={d}>
                    {MATERIAL_EVENT_DISPOSITION_LABELS[d]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">Note</span>
              <textarea
                className="min-h-[60px] px-space-2 py-space-1 border border-border-light rounded-md text-small"
                value={dispositionNote}
                onChange={(e) => setDispositionNote(e.target.value)}
              />
            </label>
            <div className="flex gap-space-2 justify-end">
              <Button variant="tertiary" size="sm" onClick={() => setPanel("none")}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void submitDisposition()}
                disabled={setDisposition.isPending}
                data-testid="material-event-disposition-submit"
              >
                Update disposition
              </Button>
            </div>
          </section>
        )}

        {panel === "escalate" && (
          <section
            className="rounded-md border border-border-light p-space-3 flex flex-col gap-space-2"
            data-testid="material-event-escalate-form"
          >
            <h4 className="font-display font-semibold text-h4 m-0">
              Escalate to compliance exception
            </h4>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Program-document citation
              </span>
              <input
                className="h-9 px-space-2 border border-border-light rounded-md text-small"
                value={citation}
                onChange={(e) => setCitation(e.target.value)}
                data-testid="material-event-citation"
              />
            </label>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Remediation plan (optional)
              </span>
              <textarea
                className="min-h-[60px] px-space-2 py-space-1 border border-border-light rounded-md text-small"
                value={remediationPlan}
                onChange={(e) => setRemediationPlan(e.target.value)}
              />
            </label>
            <div className="flex gap-space-2 justify-end">
              <Button variant="tertiary" size="sm" onClick={() => setPanel("none")}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void submitEscalate()}
                disabled={escalate.isPending}
                data-testid="material-event-escalate-submit"
              >
                Escalate
              </Button>
            </div>
          </section>
        )}

        {panel === "link" && (
          <section
            className="rounded-md border border-border-light p-space-3 flex flex-col gap-space-2"
            data-testid="material-event-link-form"
          >
            <h4 className="font-display font-semibold text-h4 m-0">
              Link payment LoanEvent
            </h4>
            <label className="flex flex-col gap-space-1 text-caption">
              <span className="text-gray-mid uppercase tracking-label">
                Loan event GUID
              </span>
              <input
                className="h-9 px-space-2 border border-border-light rounded-md text-small font-mono"
                value={loanEventId}
                onChange={(e) => setLoanEventId(e.target.value)}
              />
            </label>
            <div className="flex gap-space-2 justify-end">
              <Button variant="tertiary" size="sm" onClick={() => setPanel("none")}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void submitLink()}
                disabled={linkLoanEvent.isPending}
              >
                Link event
              </Button>
            </div>
          </section>
        )}

        {error && panel !== "none" && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}

        <section>
          <h4 className="font-display font-semibold text-h4 m-0 mb-space-2">
            Notice history
          </h4>
          {notices.isLoading ? (
            <p className="text-caption text-gray-mid italic m-0">Loading…</p>
          ) : (notices.data?.length ?? 0) === 0 ? (
            <p className="text-caption text-gray-mid m-0">
              No notices recorded.
            </p>
          ) : (
            <ul
              className="flex flex-col gap-space-1 m-0 p-0 list-none"
              data-testid="material-event-notice-history"
            >
              {(notices.data ?? []).map((n) => (
                <li
                  key={n.id}
                  className="text-small border-l-2 border-border-light pl-space-2"
                >
                  <div className="font-semibold">{n.notifiedParty}</div>
                  <div className="text-caption text-gray-mid">
                    {formatDate(n.notifiedAt)}
                    {n.channel ? ` · ${n.channel}` : ""}
                    {n.requiredByDate ? ` · req. by ${formatDate(n.requiredByDate)}` : ""}
                  </div>
                  {n.note && <div className="text-caption mt-space-1">{n.note}</div>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </ResourceDrawer>
  );
}
